import { Injectable, inject } from "@angular/core";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import { executeQueryJSON } from "@arcgis/core/rest/query";
import MapView from "@arcgis/core/views/MapView";
import {
    Observable,
    catchError,
    forkJoin,
    from,
    map,
    of
} from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { GeometryType, LayerType } from "../map.models";
import { IMapGeometry, IMapGeometryWicket, IMapLayer, IMapSettings } from "../map.settings";
import { IMapService } from "./map-select.service";
import { SpatialService } from "./spatial.service";
import type { QueryProperties } from "@arcgis/core/rest/support/Query";

export interface IMapQuery {
    layerid: any;
    geometry?: IMapGeometry;
    arcGeometry?: any;
    sublayerqueries: IMapSublayerQuery[];
}

export interface IMapSublayerQuery {
    layerid: any;
    layertitle?: string;
    params?: IQueryLayerParam[];
}

export interface IQueryLayerParam {
    name: string;
    value: string;
    isLike?: boolean;
}

export interface IMapQueryResult {
    layerid: any;
    sublayerid: any;
    sublayername: string;
    displayFieldName: string;
    geometries: IMapGeometry[];
}

@Injectable({
    providedIn: "root"
})
export class MapQueryService implements IMapService {
    private readonly spatialService = inject(SpatialService);

    private mapsettings: IMapSettings;

    private arcView: MapView;
    private readonly queryTimeoutMs = 20000;

    init(mapsettings: IMapSettings, arcView: MapView) {
        this.mapsettings = mapsettings;
        this.arcView = arcView;
    }

    queryAllVisibleSublayers(raskGeometry: IMapGeometry): Observable<IMapQueryResult[][]> {
        const layersWithSublayers = this.arcView.layerViews.toArray()
            .filter(xl => ((xl.layer) as MapImageLayer).allSublayers?.length > 0);
        return forkJoin(layersWithSublayers.map(lv => this.queryAllSublayers(lv.layer.id, null, raskGeometry)));
    }

    queryAllSublayers(layerid: string, arcGeometry: any, raskGeometry: IMapGeometry): Observable<IMapQueryResult[]> {
        const arcGisGeometry = ObjectUtils.isNullOrUndefined(arcGeometry) ? this.spatialService.toArcGis(raskGeometry)?.geometry : arcGeometry;
        const arcLayer = this.arcView.layerViews.find(x => x.layer.id === layerid)?.layer;
        if (arcLayer && arcLayer.type === LayerType.mapImage) {
            const imageLayer = arcLayer as MapImageLayer;
            const sublayers = imageLayer.allSublayers.toArray();
            const visibleSubLayers = sublayers.filter(x => x.visible === true && !(x?.sublayers?.length));

            if (visibleSubLayers?.length > 0) {
                const slqueries = visibleSubLayers.map(sl => ({ layerid: sl.id,
                    layertitle: sl.title }));
                const query = {
                    layerid: arcLayer.id,
                    arcGeometry: arcGisGeometry,
                    sublayerqueries: slqueries
                } as IMapQuery;
                return this.queryLayer(query);
            } else {
                return of([] as IMapQueryResult[]);
            }
        }
        return of([] as IMapQueryResult[]);
    }

    queryLayer(query: IMapQuery): Observable<IMapQueryResult[]> {
        const layer = this.mapsettings.featurelayers.find(x => x.id === query.layerid);
        const arcGeometry = this.getQueryArcGeometry(query);
        const distance = arcGeometry?.type !== GeometryType.polygon && arcGeometry?.type !== GeometryType.extent ? 3 * this.arcView.resolution : null;
        const observables = query.sublayerqueries.map(sublayer => this.querySublayer(
            layer,
            sublayer.layerid,
            sublayer.layertitle,
            arcGeometry,
            sublayer.params,
            distance));

        return forkJoin(observables);
    }

    private fromArcgis(feature: any): IMapGeometryWicket {
        return this.spatialService.toRask(feature, true);
    }

    private querySublayer(
        rasklayer: IMapLayer,
        sublayerid: any,
        sublayertitle: any,
        arcGeometry: any = null,
        queryParams: IQueryLayerParam[] = null,
        distance: number = null): Observable<IMapQueryResult> {
        const arcLayer = this.arcView.layerViews.find(x => x.layer.id === rasklayer.id)?.layer;
        // match both id and title (where title is available)
        const sublayer = (arcLayer as any)?.allSublayers?.find(sl => sl.id === sublayerid && (ObjectUtils.isNullOrUndefined(sublayertitle) || sl.title === sublayertitle));
        const query = {
            outFields: ["*"],
            returnGeometry: true
        } as QueryProperties;
        if (ObjectUtils.hasValue(arcGeometry)) {
            query.spatialRelationship = "intersects";
            query.geometry = arcGeometry;
            query.units = "meters";
            query.distance = distance;
        }
        if (queryParams?.length > 0) {
            let filter = "";
            queryParams.forEach(param => {
                if (filter.length > 0) {
                    filter = `${filter} AND `;
                }
                if (param.isLike) {
                    filter = `${filter}${param.name} LIKE '${param.value}%'`;
                } else {
                    filter = `${filter}${param.name} = '${param.value}'`;
                }
            });
            query.where = filter;
        } else if (sublayer.definitionExpression?.length > 0) {
            query.where = sublayer.definitionExpression;
        } else {
            // https://community.esri.com/t5/arcgis-viewer-for-flex-questions/getting-invalid-or-missing-input-parameters-error/m-p/107056#M3850
            query.where = "1 = 1";
        }

        const options = { timeout: this.queryTimeoutMs };

        return from(executeQueryJSON(sublayer.url, query, options)).pipe(map(r => ({
            geometries: r.features.map((result) => this.fromArcgis(result)),
            layerid: arcLayer.id,
            sublayerid : sublayerid,
            sublayername: sublayertitle,
            displayFieldName: r.displayFieldName
        })), catchError(e => {
            console.error(e);
            return of({ geometries: [],
                layerid: sublayerid } as IMapQueryResult);
        }));
    }

    private mouseToPoint(mouse): any {
        return {
            x: mouse.x,
            y: mouse.y,
            type: "point",
            spatialReference: this.spatialService.getSpatialReference()
        };
    }

    private getQueryArcGeometry(query: IMapQuery) {
        if (query.arcGeometry) {
            if (ObjectUtils.isNullOrUndefined(query.arcGeometry.type) && query.arcGeometry.x) {
                return {
                    x: query.arcGeometry.x,
                    y: query.arcGeometry.y,
                    type: "point",
                    spatialReference: this.spatialService.getSpatialReference()
                };
            }
            return query.arcGeometry;
        }
        if (query.geometry) {
            return this.spatialService.toArcGis(query.geometry).geometry;
        }
        return undefined;
    }
}
